import React, { ReactNode, useContext } from 'react';
import PageLayout from '../pagelayout';
import { dateTimeNorwegianLocale, todayAsFullDate } from '../date.utils';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { ToolbarLink } from '../pagelayout/toolbarLink';
import * as css from './dashboard.module.scss';
import translations from './dashboard.i18n';
import { LanguageContext } from '../i18n/LanguageStore';
import { UhaUserContext } from '../user/UhaUserStore';
import { useLoader } from '../api/useLoader';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import Spinner from '../components/spinner/spinner';
import {
  FSFeilDto,
  FSSyncStatusDto,
  FSSyncStatusPerTypeDto,
  FSSyncTypeDto,
} from '../api/generated/studiested-generated-api';
import { useFormatMessageFor } from '../i18n/translation';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import classNames from 'classnames';
import { sortNotInPlace } from '../util/sort';
import Button from '../components/button';
import { NotificationFunctionContext } from '../components/notifications/notificationProvider';
import useAddErrorNotificationCallback from '../hooks/useAddErrorNotificationCallback';

const Dashboard = () => {
  const { selectedLanguage } = useContext(LanguageContext);
  const { uhaUser: bruker } = useContext(UhaUserContext);
  const labels = useFormatMessageFor({
    ...translations,
    welcome: {
      ...translations.welcome,
      values: {
        name: bruker ? `${bruker.fornavn} ${bruker.etternavn}` : 'UKJENT',
      },
    },
  });

  const toolbarLink: React.ReactNode = ToolbarLink({
    title: labels.linkToCalendar,
    exact: true,
    path: '/calendar',
    icon: <DateRangeIcon />,
  });

  return (
    <PageLayout isIndented title={labels.title} actionItems={toolbarLink}>
      <div className={css.dashboard}>
        <Card>
          <div className={css.leftContainer}>
            <p className={css.date}>{todayAsFullDate(selectedLanguage)}</p>
            <CardTitle>{labels.welcome}</CardTitle>
            <p>{labels.desc}</p>
          </div>
          <div className={css.rightContainer}>
            <div className={css.officeIllustration} />
          </div>
        </Card>
        <FsSyncStatusWrapper />
        <FsFeilmeldingerWrapper />
      </div>
    </PageLayout>
  );
};

interface Children {
  children: ReactNode;
}

interface CardProps extends Children {
  title?: string;
  subtitle?: string;
}

const Card = ({ children, title, subtitle }: CardProps) => (
  <div className={css.card}>
    {title && <CardTitle>{title}</CardTitle>}
    {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
    {children}
  </div>
);

const CardTitle = ({ children }: Children) => (
  <h2 className={css.title}>{children}</h2>
);
const CardSubtitle = ({ children }: Children) => (
  <h3 className={css.subtitle}>{children}</h3>
);

interface CardParagraphProps extends Children {
  className?: string;
}

const CardParagraph = ({ children, className }: CardParagraphProps) => (
  <p className={classNames(css.content, className)}>{children}</p>
);

const CardSpinner = () => (
  <CardParagraph>
    <Spinner height={'5em'} />
  </CardParagraph>
);

const SyncTableHeaders = {
  TYPE: 'TYPE',
  STATUS: 'STATUS',
  NESTE_KJORING: 'NESTE_KJORING',
  SISTE_VELLYKKET: 'SISTE_VELLYKKET',
} as const;

const useSyncTableHeadersToTranslation = (): {
  [key in keyof typeof SyncTableHeaders]: string;
} => {
  const labels = useFormatMessageFor(translations);

  return {
    NESTE_KJORING: labels.headerNesteKjoring,
    SISTE_VELLYKKET: labels.headerSisteVellykket,
    STATUS: labels.headerStatus,
    TYPE: labels.headerType,
  };
};

const useSyncStatusTranslation = (
  status: FSSyncStatusDto | undefined,
): string => {
  const labels = useFormatMessageFor(translations);

  switch (status) {
    case FSSyncStatusDto.Ok:
      return labels.syncStatusOk;
    case FSSyncStatusDto.FeiletSisteDogn:
      return labels.syncStatusFeiletSisteDogn;
    case FSSyncStatusDto.Feilet:
      return labels.syncStatusFeilet;
    case undefined:
      return labels.syncStatusUkjent;
  }
};

const useSyncStatusStyle = (status: FSSyncStatusDto | undefined): string => {
  switch (status) {
    case FSSyncStatusDto.Ok:
      return css.statusOk;
    case FSSyncStatusDto.FeiletSisteDogn:
      return css.statusFeiletDogn;
    case FSSyncStatusDto.Feilet:
      return css.statusFeilet;
    case undefined:
      return '';
  }
};

const useSyncTypeTranslation = (type: FSSyncTypeDto): string => {
  const labels = useFormatMessageFor(translations);

  switch (type) {
    case FSSyncTypeDto.Emne:
      return labels.syncTypeEmne;
    case FSSyncTypeDto.Institusjon:
      return labels.syncTypeInstitusjon;
    case FSSyncTypeDto.Praksisavtale:
      return labels.syncTypePraksisavtale;
    case FSSyncTypeDto.Praksissted:
      return labels.syncTypePraksissted;
    case FSSyncTypeDto.Praksistype:
      return labels.syncTypePraksistype;
    case FSSyncTypeDto.Student:
      return labels.syncTypeStudent;
    case FSSyncTypeDto.Studiested:
      return labels.syncTypeStudiested;
    case FSSyncTypeDto.Undervisningsenhet:
      return labels.syncTypeUndervisningsenhet;
  }
};

const compareStatusType = (
  a: FSSyncStatusPerTypeDto,
  b: FSSyncStatusPerTypeDto,
): number => a.type.localeCompare(b.type);

const FsSyncStatusWrapper = () => {
  const labels = useFormatMessageFor(translations);

  return (
    <Card
      title={labels.statusFsSyncTitle}
      subtitle={labels.statusFsSyncSubtitle}
    >
      <FsSyncStatus />
    </Card>
  );
};

const FsSyncStatus = () => {
  const headerTranslations = useSyncTableHeadersToTranslation();
  const headers = Object.keys(SyncTableHeaders).map((header) => {
    const className = header === SyncTableHeaders.STATUS && css.statusHeader;
    const align =
      header === SyncTableHeaders.NESTE_KJORING ||
      header === SyncTableHeaders.SISTE_VELLYKKET
        ? 'end'
        : 'start';

    return (
      <TableCellTh key={header} className={className} align={align}>
        {headerTranslations[header as keyof typeof SyncTableHeaders]}
      </TableCellTh>
    );
  });

  const { dashbordApi } = useContext(StudiestedApiContext);
  const { data, loading } = useLoader(
    () => dashbordApi.dashbordFsstatusGet(),
    [],
  );

  if (loading) return <CardSpinner />;
  if (!data) return <></>;

  const rows = sortNotInPlace(data.syncStatus, compareStatusType).map(
    (syncStatusType) => (
      <FsSyncStatusRow
        synStatusType={syncStatusType}
        key={syncStatusType.type}
      />
    ),
  );

  return (
    <CardParagraph>
      <Table>
        <TableHeader>
          <TableRow top>{headers}</TableRow>
        </TableHeader>
        <TableMain>{rows}</TableMain>
      </Table>
    </CardParagraph>
  );
};

interface FSSyncStatusRow {
  synStatusType: FSSyncStatusPerTypeDto;
}

const FsSyncStatusRow = ({
  synStatusType: { status, type, sisteVellykket, nesteKjoring },
}: FSSyncStatusRow) => {
  const headerTranslations = useSyncTableHeadersToTranslation();
  const typeTranslation = useSyncTypeTranslation(type);
  const statusTranslation = useSyncStatusTranslation(status);
  const nesteKjoringFormatted = dateTimeNorwegianLocale(new Date(nesteKjoring));
  const sisteVellykketFormatted = sisteVellykket
    ? dateTimeNorwegianLocale(new Date(sisteVellykket))
    : '';

  return (
    <TableRow>
      <TableCellTd header={headerTranslations[SyncTableHeaders.TYPE]}>
        {typeTranslation}
      </TableCellTd>
      <TableCellTd header={headerTranslations[SyncTableHeaders.STATUS]}>
        <FsSyncStatusState status={status}>
          {statusTranslation}
        </FsSyncStatusState>
      </TableCellTd>
      <TableCellTd
        header={headerTranslations[SyncTableHeaders.NESTE_KJORING]}
        align="end"
      >
        {nesteKjoringFormatted}
      </TableCellTd>
      <TableCellTd
        header={headerTranslations[SyncTableHeaders.SISTE_VELLYKKET]}
        align="end"
      >
        {sisteVellykketFormatted}
      </TableCellTd>
    </TableRow>
  );
};

interface FsSyncStatusColorProps extends Children {
  status: FSSyncStatusDto | undefined;
}

const FsSyncStatusState = ({ status, children }: FsSyncStatusColorProps) => {
  const color = useSyncStatusStyle(status);

  return (
    <div className={css.statusState}>
      {children}
      <div className={classNames(css.statusColor, color)} />
    </div>
  );
};

const FsFeilmeldingerWrapper = () => {
  const labels = useFormatMessageFor(translations);

  return (
    <Card
      title={labels.feilmeldingerFsTitle}
      subtitle={labels.feilmeldingerFsSubtitle}
    >
      <FsFeilmeldinger />
    </Card>
  );
};

const FeilmeldingTableHeaders = {
  FEILMELDING: 'FEILMELDING',
  FJERN: 'FJERN',
} as const;

const useFeilmeldingTableHeadersToTranslation = (): {
  [key in keyof typeof FeilmeldingTableHeaders]: string;
} => {
  const labels = useFormatMessageFor(translations);

  return {
    FEILMELDING: labels.headerFeilmelding,
    FJERN: labels.headerFjern,
  };
};

const FsFeilmeldinger = () => {
  const { dashbordApi } = useContext(StudiestedApiContext);
  const {
    data: feilmeldinger,
    loading,
    reload,
  } = useLoader(() => dashbordApi.dashbordFsfeilGet(), []);

  const headerTranslations = useFeilmeldingTableHeadersToTranslation();

  if (loading) return <CardSpinner />;
  if (!feilmeldinger || feilmeldinger.feil.length === 0) return <></>;

  const headers = Object.keys(FeilmeldingTableHeaders).map((header) => (
    <TableCellTh
      key={header}
      align={header === FeilmeldingTableHeaders.FJERN ? 'right' : 'left'}
    >
      {headerTranslations[header as keyof typeof FeilmeldingTableHeaders]}
    </TableCellTh>
  ));

  return (
    <CardParagraph className={css.feilmeldingerParagraph}>
      <Table>
        <TableHeader>
          <TableRow top>{headers}</TableRow>
        </TableHeader>
        <TableMain>
          {feilmeldinger.feil
            .filter(({ feilmelding }) => feilmelding)
            .map((feilmelding, index) => (
              <FsFeilmelding
                feil={feilmelding}
                key={`feilmelding-${index}`}
                reloadFeilmeldinger={reload}
              />
            ))}
        </TableMain>
      </Table>
    </CardParagraph>
  );
};

interface FsFeilmeldingProps {
  feil: FSFeilDto;
  reloadFeilmeldinger: () => void;
}

const FsFeilmelding = ({
  feil: { feilmelding },
  reloadFeilmeldinger,
}: FsFeilmeldingProps) => {
  const labels = useFormatMessageFor(translations);
  const { dashbordApi } = useContext(StudiestedApiContext);
  const { addSuccessNotification } = useContext(NotificationFunctionContext);
  const errorNotificationCallback = useAddErrorNotificationCallback();
  const headerTranslations = useFeilmeldingTableHeadersToTranslation();

  if (!feilmelding) return <></>;

  const { message, feilmeldingId } = feilmelding;

  const onClick = () => {
    dashbordApi
      .dashbordFsstatusFsdbfeilmeldingIdDelete({
        pathParams: { fsdbfeilmeldingId: feilmeldingId },
      })
      .then(() => {
        addSuccessNotification(labels.clearSuccess);
        reloadFeilmeldinger();
      })
      .catch(errorNotificationCallback);
  };

  return (
    <TableRow>
      <TableCellTd
        header={headerTranslations[FeilmeldingTableHeaders.FEILMELDING]}
      >
        {message}
      </TableCellTd>
      <TableCellTd
        header={headerTranslations[FeilmeldingTableHeaders.FJERN]}
        align="right"
      >
        <Button.Secondary className={css.clearBtn} onClick={onClick}>
          {labels.clearForAllUsers}
        </Button.Secondary>
      </TableCellTd>
    </TableRow>
  );
};

export default Dashboard;
